import {
  LinearProgress,
  Table,
  TableBody,
  TableHead,
  TableRow,
  Typography,
  Box,
  TableFooter,
  TablePagination,
  IconButton,
  Drawer,
} from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../Redux/hooks";
import {
  NoDataContainer,
  pageSize,
  paginationLabel,
  StyledTableCell,
  TableBorderRadiusTopLeftRight,
  TablePaginationStyle,
} from "../Common/styles/table";
import {
  setAiSearchQuery,
  setCurrentSearchId,
  setFilters,
  setSearchParticipantsPage,
} from "../../Redux/reducers/aiSearchSlice";
import { useEffect, useMemo, useState } from "react";
import {
  fetchParticipantsList,
  fetchParticipantsListWithAI,
} from "../../Redux/actions/aiSearchAction";
import { useNavigate } from "react-router-dom";
import GlobalFilterModal from "./GlobalFilterModal";
import FilterAltIcon from "@mui/icons-material/FilterAlt";

const ParticipantList = ({ toggle }: any) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [showFilter, setShowFilter] = useState(false);

  const closeFilter = () => {
    setShowFilter(false);
  };

  const openFilter = () => {
    setShowFilter(true);
  };

  const handlePageChange = (_1: any, newPage: number) => {
    dispatch(setSearchParticipantsPage(newPage));
  };

  const participantsLoader = useAppSelector(
    (state) => state.aiSearch.participantsLoader
  );
  const searchParticipantsData = useAppSelector(
    (state) => state.aiSearch.searchParticipantsData
  );
  const searchParticipantsCount = useAppSelector(
    (state) => state.aiSearch.searchParticipantsCount
  );
  const participantPage = useAppSelector(
    (state) => state.aiSearch.participantPage
  );
  const currentSearchId = useAppSelector(
    (state) => state.aiSearch.currentSearchId
  );
  const filters = useAppSelector((state) => state.aiSearch.filters);

  useEffect(() => {
    if (!currentSearchId) {
      dispatch(fetchParticipantsList(participantPage, filters));
    } else {
      dispatch(fetchParticipantsListWithAI(currentSearchId, participantPage));
    }
  }, [dispatch, currentSearchId, participantPage, filters, toggle]);

  const handleClick = (row: any) => {
    navigate(
      `/app/participants/${row?.participantId}?participant=${
        row?.name || row?.email
      }`
    );
  };

  const hasFilter = useMemo(() => {
    return Object.values(filters).some((value) => value !== "");
  }, [filters]);

  return (
    <Box sx={{ p: 2, backgroundColor: "#fff", borderRadius: "10px" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        {(currentSearchId || hasFilter) && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Typography fontWeight="medium" color="text.primary">
              {currentSearchId ? "AI Search Results" : "Filtered Results"}
            </Typography>
            <Typography
              sx={{ cursor: "pointer", color: "primary.main" }}
              onClick={() => {
                if (currentSearchId) {
                  dispatch(setCurrentSearchId(""));
                } else {
                  dispatch(setFilters({}));
                }
              }}
            >
              Clear
            </Typography>
          </Box>
        )}
        <Box
          sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1.5 }}
        >
          {!currentSearchId && (
            <IconButton
              onClick={openFilter}
              sx={{
                height: "48px",
                width: "48px",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: "8px",
              }}
            >
              <FilterAltIcon
                color={hasFilter ? "primary" : "disabled"}
                sx={{ fontSize: "24px" }}
              />
            </IconButton>
          )}
        </Box>
      </Box>
      <Box sx={{ overflowX: "auto" }}>
        <Table sx={TableBorderRadiusTopLeftRight}>
          <TableHead>
            <TableRow>
              <StyledTableCell>Email</StyledTableCell>
              <StyledTableCell>Name</StyledTableCell>
              <StyledTableCell>Created At</StyledTableCell>
            </TableRow>
          </TableHead>
          {!participantsLoader && searchParticipantsCount > 0 && (
            <>
              <TableBody>
                {searchParticipantsData?.map((row: any, index: number) => (
                  <TableRow key={index}>
                    <StyledTableCell
                      sx={{ cursor: "pointer" }}
                      onClick={() => handleClick(row)}
                    >
                      {row?.email || "-"}
                    </StyledTableCell>
                    <StyledTableCell>{row?.name || "-"}</StyledTableCell>
                    <StyledTableCell>{row?.createdAt || "-"}</StyledTableCell>
                  </TableRow>
                ))}
              </TableBody>
              {searchParticipantsCount > 10 && (
                <TableFooter>
                  <TableRow>
                    <TablePagination
                      sx={TablePaginationStyle}
                      count={searchParticipantsCount}
                      page={participantPage}
                      rowsPerPage={pageSize}
                      rowsPerPageOptions={[pageSize]}
                      onPageChange={handlePageChange}
                      labelDisplayedRows={paginationLabel}
                    />
                  </TableRow>
                </TableFooter>
              )}
            </>
          )}
        </Table>
        {!participantsLoader && searchParticipantsCount === 0 && (
          <NoDataContainer>
            <Typography variant="body1" color="gray">
              No Data
            </Typography>
          </NoDataContainer>
        )}
        {participantsLoader && (
          <Box sx={{ width: "100%" }}>
            <LinearProgress />
          </Box>
        )}
        {showFilter && (
          <Drawer open={showFilter} onClose={closeFilter} anchor="right">
            <GlobalFilterModal closeModal={closeFilter} />
          </Drawer>
        )}
      </Box>
    </Box>
  );
};

export default ParticipantList;
